import React from 'react';
import { Query } from 'react-apollo';
import { map, partition, flatten } from 'lodash';

import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ChatActions from 'chat/actions/ChatActions';
import CoachingGetHelpButton from 'coaching/components/CoachingGetHelpButton';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import SingleChatStreamView, {
  SingleChatStreamViewMember,
} from 'chat/components/SingleChatStreamView';

import LOAD_CHATS from 'chat/queries/LoadChatsQuery.gql';

import {
  LoadChats,
  LoadChatsVariables,
  LoadChats_chatStreams_memberships_ChatStream_nodes_ChatStream_user_ChatStream,
} from 'chat/queries/__generated__/LoadChats';

type Props = {
  params: { streamUuid: string };
};

const eventPrefix = 'coaching.stream_view';

function getMemberData(
  users: LoadChats_chatStreams_memberships_ChatStream_nodes_ChatStream_user_ChatStream[],
  currentUser: any
) {
  const [currentMembers, otherMembers] = partition(
    users,
    user => user.uuid === currentUser.getUuid()
  );
  const currentMember = currentMembers[0];
  if (!currentMember) {
    return undefined;
  }
  const currentMemberData = {
    displayName: 'You',
    initials: currentMember.initials ?? undefined,
    profilePictureUrl: currentMember.profile_picture_url ?? undefined,
    role: undefined,
  };
  return flatten<SingleChatStreamViewMember>([
    otherMembers.map(member => ({
      displayName: member.coachingMetadata?.coachingDisplayName ?? member.name ?? undefined,
      initials: member.initials ?? undefined,
      profilePictureUrl: member.profile_picture_url ?? undefined,
      // we only want to show the user's role if they are a student and if the viewing user is a coach
      // because we don't think it is useful information for anyone else in the chat
      role:
        currentMember.coachingMetadata?.isRemindCoach && member.role === 'student'
          ? member.role
          : undefined,
    })),
    currentMemberData,
  ]);
}

function isCoach(
  users: LoadChats_chatStreams_memberships_ChatStream_nodes_ChatStream_user_ChatStream[],
  currentUser: any
) {
  const currentMember = users.find(user => user.uuid === currentUser.getUuid());
  if (!currentMember) {
    return false;
  }

  return !!currentMember.coachingMetadata?.isRemindCoach;
}

export default function CoachingChatStreamContainer({ params: { streamUuid } }: Props) {
  const currentUser = CurrentUser.isSignedIn() ? CurrentUser.getUser() : undefined;
  if (!currentUser) {
    return (
      // this should never happen because we have `RequireUser` on this route
      <ErrorReporter error="CoachingChatStreamContainer - tried to render without being logged in">
        Oops! It looks like you aren’t logged in. Please try refreshing or logging in again.
      </ErrorReporter>
    );
  }

  return (
    <Query<LoadChats, LoadChatsVariables>
      query={LOAD_CHATS}
      variables={{ includeCoachingMetadata: true, chatUuids: [streamUuid] }}>
      {({ loading, data, error }) => {
        const chatStream = data?.chatStreams?.[0];
        if (error) {
          console.error(`Failed to load coaching chat stream uuid=${streamUuid}`, error);
        }
        if (loading || !chatStream) {
          return <CenteredLoadingIndicator />;
        }
        const chat = chatStream && ChatActions.getChatFromChatStream(chatStream);
        const users = map(
          chatStream.memberships.nodes,
          node => node.user
        ) as LoadChats_chatStreams_memberships_ChatStream_nodes_ChatStream_user_ChatStream[];
        const chatMemberData = getMemberData(users, currentUser);
        if (!chatMemberData) {
          return (
            <ErrorReporter error="CoachingChatStreamContainer - tried to render stream that the current user is not a part of">
              Oops! It looks like you aren’t a part of this chat thread. Please contact
              coaching@remind101.com if this is a mistake.
            </ErrorReporter>
          );
        }
        return (
          <SingleChatStreamView
            chat={chat}
            streamUuid={streamUuid}
            viewEventKey={`${eventPrefix}.view`}
            headerActions={[
              <CoachingGetHelpButton
                eventKey={`${eventPrefix}.get_help.click`}
                isCoach={isCoach(users, currentUser)}
                key="helpButton"
              />,
            ]}
            chatMemberData={chatMemberData}
          />
        );
      }}
    </Query>
  );
}
